// One deal: the clauses it bills under, what they came to this period, the
// facts they read, and what it consumed.

import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { dealHasCoordinates, dealHasLocation, dealInheritsTerms, splitMinor } from "@crm/shared";
import type {
  DealFact,
  DealListItem,
  DealStatementLine,
  DealStatementPrepaid,
  PrepaidEntitlement,
  TermLine,
} from "@crm/shared";
import styles from "./DealDetailPage.module.css";
import { Badge, toneFor } from "../../design/Badge.js";
import { Card, CardBody } from "../../design/Card.js";
import { Notice } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { DealNotesPanel } from "../notes/DealNotesPanel.js";
import {
  formatCalendarDate,
  formatDateRange,
  formatOptionalCalendarDate,
  todayIsoDate,
} from "../../format/calendarDate.js";
import { countryLabel } from "../../format/countries.js";
import { formatMoney, formatPercent } from "../../format/money.js";
import { dealStateColor, dealStateLabel, invoiceRecipientLabel } from "../../format/labels.js";
import { DeleteDialog } from "../../forms/DeleteDialog.js";
import { useDealFactMutations, useTermLineMutations } from "../../api/mutations.js";
import { useHasRole } from "../../auth/useAuth.js";
import { FactEntryDialog, type FactPrefill } from "../facts/FactEntryDialog.js";
import { DealFormDialog } from "./DealFormDialog.js";
import { DealFactsSection } from "./DealFactsSection.js";
import { DealHistorySection } from "./DealHistorySection.js";
import { PrepaidEntitlementsSection } from "./PrepaidEntitlementsSection.js";
import { TenantBindingsCard } from "./TenantBindingsCard.js";
import { BillingContactsCard } from "../partners/BillingContactsCard.js";
import { ImplementationFeeCard } from "./ImplementationFeeCard.js";
import { TermLineFormDialog, type TermLinePrefill } from "./TermLineFormDialog.js";
import { TermLinesSection } from "./TermLinesSection.js";
import { hasMilestoneClause, splitCoverage, type SplitCoverage } from "./dealClauses.js";
import {
  useDeal,
  useDealStatement,
  usePrepaidEntitlements,
  useTermLines,
} from "../../api/queries.js";

type DealScreenAction =
  | { kind: "edit_deal" }
  // `prefill` carries the implementation-fee card's suggestion into the
  // ordinary term-line form. Absent for the plain "Add a term line" button,
  // which is the same dialog with an empty draft — one form, two entry points,
  // rather than a second form that could drift from it.
  | { kind: "create_line"; prefill?: TermLinePrefill }
  | { kind: "edit_line"; line: TermLine }
  | { kind: "delete_line"; line: TermLine }
  // ONE dialog for any figure a person supplies, opened from two places: a
  // statement line that reports no figure, and the section's own button. The
  // prefill is the difference between them.
  | { kind: "record_fact"; prefill: FactPrefill }
  | { kind: "delete_fact"; fact: DealFact };

export function DealDetailPage(): ReactNode {
  const { dealId } = useParams({ from: "/deals/$dealId" });
  const deal = useDeal(dealId);

  return (
    <QueryBoundary query={deal} label="this deal">
      {(item) => <DealDetail deal={item} />}
    </QueryBoundary>
  );
}

function DealDetail({ deal }: { deal: DealListItem }): ReactNode {
  const canEdit = useHasRole("crm_editor");
  const [action, setAction] = useState<DealScreenAction | null>(null);
  /**
   * Which month the figures on this page are for.
   *
   * THE MONTH THE READER IS IN. A deal page answers "what is this deal doing",
   * and an open month is the only month that question is about. "What did
   * August come to" is the close screen's question, and the close screen is
   * where it is asked — this page opening on the month just gone made every
   * visitor step forward before they could read it.
   */
  const [period, setPeriod] = useState<string>(() => todayIsoDate().slice(0, 7));
  /** A line the form is trying out. Cleared when the dialog closes or saves. */
  const [preview, setPreview] = useState<DealStatementLine | null>(null);

  // The shell's top bar is this screen's heading, so the deal names itself
  // there rather than repeating a title inside the first card. The agreement
  // named is the EFFECTIVE one — the terms this deal actually bills under,
  // which for an inheriting deal is not one it points at.
  usePageChrome({
    title: deal.customer_name,
  });

  // `?note=` — the note a search result sent the reader here to read.
  const { note: openNoteId } = useSearch({ from: "/deals/$dealId" });
  const lineMutations = useTermLineMutations();
  const factMutations = useDealFactMutations();

  // The clauses, read here as well as in the section under the SAME key, so one
  // request answers both. Two panels state a claim about this deal that only
  // its clauses can settle: whether an absent agreement breaks anything, and
  // whether an implementation fee is part of this engagement at all.
  const lines = useTermLines(deal.id);
  const clauses = lines.data?.items;
  const coverage = clauses === undefined ? null : splitCoverage(clauses);
  const milestoneClause = clauses !== undefined && hasMilestoneClause(clauses);

  // Shared with `TermLinesSection` through the query cache — the same key, so
  // one request answers both — because the pool positions come off the same
  // statement the section draws.
  const statement = useDealStatement(deal.id, period);
  // Read here as well as in the section, under the same key, because an
  // entitlement adjustment needs a block to name — and the fact dialog is
  // opened from this page rather than from the section that lists them.
  const pools = usePrepaidEntitlements({ kind: "deal", id: deal.id });
  const answer = statement.data;
  const positions = new Map<string, DealStatementPrepaid>();
  if (answer !== undefined && answer.kind === "ready") {
    for (const line of answer.statement.lines) {
      if (line.prepaid !== null) positions.set(line.prepaid.entitlement_id, line.prepaid);
    }
  }

  // Every editable value opens the same form. Null for a reader without the
  // role: the dashed underline is a promise, and offering it to someone whose
  // save would be refused is worse than showing plain text.
  const editDeal = canEdit ? () => setAction({ kind: "edit_deal" }) : null;

  return (
    <div className={styles.page}>
      <div className={styles.breadcrumb}>
        <Link to="/deals">Deals</Link>
        <span aria-hidden="true">/</span>
        <span className={styles.crumbCurrent}>{deal.customer_name}</span>
        <div className={styles.breadcrumbActions}>
          {/* What this deal's tenants cost, month by month. It is a screen of
              its own rather than a card here, because a month of cost has its
              own controls — a period, a breakdown and the deployments in
              scope — and none of them belong to the rest of this page. */}
          <Link to="/deals/$dealId/usage" params={{ dealId: deal.id }}>
            Usage cost →
          </Link>
        </div>
      </div>

      {/* Not a failure of this screen, so not a ProblemMessage: the deal reads
          fine, it just has no terms to bill under. Said here rather than left
          to be discovered when a report refuses to run — and said only of a
          deal it is actually true of. */}
      {deal.effective_agreement_id === null && <MissingAgreement deal={deal} coverage={coverage} />}

      <div className={styles.columns}>
        {/* THE RAIL: what this deal IS. Read far more often than it is changed,
            and none of it is worked on — so it sits beside the work rather than
            above it, where fifteen fields stretched across a wide window and
            pushed every figure below the fold. */}
        <aside className={styles.rail}>
          <Card>
            <CardBody>
              <div className={styles.fields}>
                {/* Read-only, for everyone. The pill used to double as a state
                changer; a deal's stage now moves only through Edit, where the
                change sits beside the fields that ought to move with it. */}
                <Field label="State">
                  <Badge shape="pill" tone={toneFor(dealStateColor(deal.state))}>
                    {dealStateLabel(deal.state)}
                  </Badge>
                </Field>

                {/* Immutable (D8), so no dashed underline promising an edit: every
                amount under this deal is denominated in it. */}
                <Field label="Currency">
                  <div className={styles.value}>{deal.currency}</div>
                </Field>

                <Field label="Win probability">
                  <EditableValue
                    value={formatPercent(deal.win_prob)}
                    editLabel="Edit the win probability"
                    onEdit={editDeal}
                  />
                </Field>

                <Field label="Invoice recipient">
                  <EditableValue
                    value={invoiceRecipientLabel(deal.invoice_recipient)}
                    editLabel="Edit the invoice recipient"
                    onEdit={editDeal}
                  />
                </Field>

                <Field
                  label="Start date"
                  hint={
                    deal.start_date_is_firm
                      ? "Firm — lands wholly in one pipeline month"
                      : "Range — the pipeline report spreads this deal's weighted value uniformly across every month it touches"
                  }
                >
                  <EditableValue
                    value={
                      deal.start_date_is_firm
                        ? formatCalendarDate(deal.start_date_earliest)
                        : formatDateRange(deal.start_date_earliest, deal.start_date_latest)
                    }
                    editLabel="Edit the start date"
                    onEdit={editDeal}
                  />
                </Field>

                <Field label="Contract term">
                  <EditableValue
                    value={
                      deal.contract_term_months === null
                        ? "—"
                        : `${deal.contract_term_months} months`
                    }
                    editLabel="Edit the contract term"
                    onEdit={editDeal}
                  />
                </Field>

                <Field label="Renewal date">
                  <EditableValue
                    value={formatOptionalCalendarDate(deal.renewal_date)}
                    editLabel="Edit the renewal date"
                    onEdit={editDeal}
                  />
                </Field>

                {/* The close date: stamped by the server when the state closes, and
                editable because a signing date is a fact about a contract, not
                about the day somebody clicked. Blank on an open deal. */}
                <Field label="Closed on">
                  <EditableValue
                    value={formatOptionalCalendarDate(deal.closed_on)}
                    editLabel="Edit the close date"
                    onEdit={editDeal}
                  />
                </Field>

                {/* A navigation, not an edit: the agreement behind this deal is
                where its split actually comes from. */}
                <Field label="Partner">
                  <div className={styles.value}>
                    <Link to="/partners/$partnerId" params={{ partnerId: deal.partner_id }}>
                      {deal.partner_name}
                    </Link>
                  </div>
                </Field>

                {/* The EFFECTIVE agreement, which for an inheriting deal belongs
                to a partner further up — so the badge is not decoration: it is
                the difference between terms this deal owns and terms it is
                merely borrowing, and re-parenting the partner can change the
                second one without touching the deal at all. */}
                <Field
                  label="Agreement"
                  {...(dealInheritsTerms(deal) && deal.effective_agreement_partner_name !== null
                    ? {
                        hint: `Inherited from ${deal.effective_agreement_partner_name}. Pin an agreement on the deal to override it.`,
                      }
                    : {})}
                >
                  <div className={`${styles.value} ${styles.inlineGroup}`}>
                    {deal.effective_agreement_id === null ? (
                      // Loud for a deal a report will refuse, muted for a deal
                      // whose clauses price themselves. The words differ with the
                      // colour: one is a fault, the other is a fact.
                      coverage === "needs_agreement" ? (
                        <Badge shape="pill" tone="bad">
                          No terms resolve
                        </Badge>
                      ) : (
                        <Badge shape="pill" tone="neutral">
                          No agreement on file
                        </Badge>
                      )
                    ) : (
                      <>
                        {deal.effective_agreement_name}
                        {dealInheritsTerms(deal) && (
                          <Badge shape="tag" tone="info">
                            Inherited
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                </Field>

                <CustomerLocation deal={deal} onEdit={editDeal} />

                {deal.notes !== null && deal.notes !== "" && (
                  <div className={`${styles.field} ${styles.notesField}`}>
                    <div className={styles.label}>Notes</div>
                    {/* Clamped to three lines by `.notes`; the wrapper's title
                    carries the whole of it, and the edit dialog shows it in
                    full. A title on the span rather than a new prop on
                    EditableValue: one caller wanting a tooltip is not a reason
                    to widen a shared component's API. */}
                    <span title={deal.notes}>
                      <EditableValue
                        value={deal.notes}
                        editLabel="Edit the notes"
                        onEdit={editDeal}
                        className={styles.notes}
                      />
                    </span>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          <EstimatesCard deal={deal} onEdit={editDeal} />
        </aside>

        {/* THE MAIN COLUMN: what somebody came here to do. */}
        <div className={styles.main}>
          {/* Above the term lines, because it is about one of them — the
          implementation fee this deal should carry — and the action it offers
          is to create it. Below the deal's own fields, because the account
          count it is priced from is one of those. */}
          <ImplementationFeeCard
            dealId={deal.id}
            currency={deal.currency}
            partnerName={deal.partner_name}
            hasMilestoneClause={milestoneClause}
            onAddLine={canEdit ? (prefill) => setAction({ kind: "create_line", prefill }) : null}
          />

          {/* The clauses, and what each came to in the chosen period. The section
          draws the statement panel under itself, because a rule and the figure
          it produced are one reading. */}
          <TermLinesSection
            deal={deal}
            period={period}
            onPeriodChange={setPeriod}
            canEdit={canEdit}
            preview={preview}
            onAdd={() => setAction({ kind: "create_line" })}
            onEdit={(line) => setAction({ kind: "edit_line", line })}
            onDelete={(line) => setAction({ kind: "delete_line", line })}
            onRecordFact={(line) =>
              setAction({
                kind: "record_fact",
                prefill: factFromStatementLine(deal, line, period, pools.data?.items ?? []),
              })
            }
          />

          {/* What was bought up front, and where it stands against this period's
          statement. Under the clauses, because a pool only means something
          against the lines it covers. */}
          <PrepaidEntitlementsSection
            scope={{ kind: "deal", id: deal.id }}
            positions={positions}
            ownerName={deal.customer_name}
            currency={deal.currency}
          />

          {/* Which tenants this deal's usage is rated from. A table of rows,
              so it belongs with the other tables rather than in the rail of
              single values — where it also made the rail taller than the
              window. Read-only: a binding is written by the CLI. */}
          <TenantBindingsCard dealId={deal.id} />

          {/* Who this deal's invoices go to. No figure reads it. */}
          <BillingContactsCard
            owner={{ kind: "deal", id: deal.id }}
            ownerName={deal.customer_name}
            inherited={{
              partnerName: deal.partner_name,
              effective: deal.effective_billing_contacts,
            }}
            canEdit={canEdit}
          />

          {/* What was SAID about this deal, under everything that computes from
          it. Last, because it is the only panel here that nothing else reads:
          no statement, no report and no invoice touches a note. That is also
          why it is the longest — prose is what did not fit in a column. */}
          <DealNotesPanel dealId={deal.id} openNoteId={openNoteId ?? null} />

          {/* How the deal got here: its stages, back past this CRM when HubSpot
          knew it first, and every edit. Read by the sales trends, not by billing. */}
          <DealHistorySection dealId={deal.id} currency={deal.currency} />

          {/* Under the clauses that read them: a fact is an input, and what it is
          an input TO is above it. */}
          <DealFactsSection
            deal={deal}
            canEdit={canEdit}
            onAdd={() =>
              setAction({
                kind: "record_fact",
                prefill: blankFact(deal, period, pools.data?.items ?? []),
              })
            }
            onDelete={(fact) => setAction({ kind: "delete_fact", fact })}
          />
        </div>
      </div>

      {action?.kind === "edit_deal" && (
        <DealFormDialog deal={deal} onClose={() => setAction(null)} />
      )}
      {action?.kind === "create_line" && (
        <TermLineFormDialog
          dealId={deal.id}
          currency={deal.currency}
          period={period}
          {...(action.prefill === undefined ? {} : { prefill: action.prefill })}
          onPreview={setPreview}
          onClose={() => {
            setPreview(null);
            setAction(null);
          }}
        />
      )}
      {action?.kind === "edit_line" && (
        <TermLineFormDialog
          dealId={deal.id}
          currency={deal.currency}
          period={period}
          line={action.line}
          onPreview={setPreview}
          onClose={() => {
            setPreview(null);
            setAction(null);
          }}
        />
      )}
      {action?.kind === "delete_line" && (
        <DeleteDialog
          open
          onClose={() => setAction(null)}
          title="Delete term line"
          description={`${action.line.label} will be removed, and every period it covers stops billing it. An invoice that already billed it keeps its own frozen figures — retire the line instead if you only want it to stop.`}
          record={action.line}
          remove={(id, version) => lineMutations.remove.mutateAsync({ id, version })}
          fetchCurrent={lineMutations.fetchCurrent}
          onDeleted={() => setAction(null)}
        />
      )}
      {action?.kind === "record_fact" && (
        <FactEntryDialog prefill={action.prefill} onClose={() => setAction(null)} />
      )}
      {action?.kind === "delete_fact" && (
        <DeleteDialog
          open
          onClose={() => setAction(null)}
          title="Delete this fact"
          description={`The ${action.fact.kind} recorded for ${action.fact.period} will be removed, and every clause that read it reports no figure again. To correct it instead, record it again — the same key restates the row.`}
          record={action.fact}
          remove={(id, version) => factMutations.remove.mutateAsync({ id, version })}
          fetchCurrent={factMutations.fetchCurrent}
          onDeleted={() => setAction(null)}
        />
      )}
    </div>
  );
}

/**
 * What an absent agreement actually costs this deal.
 *
 * IT DEPENDS ON THE CLAUSES, and the banner used to claim it did not: "every
 * report touching this deal fails with split_unresolvable" stood on every deal
 * with no agreement, including the ones whose every clause states `100% ours`
 * and which every report prices without complaint. A warning that is wrong on
 * the deals a reader checks first is a warning they stop reading.
 *
 * So the warning stands for the deal that needs the agreement, and the deal
 * that does not gets one muted line. Nothing at all until the clauses answer:
 * a banner that appears loud and then goes quiet is read as the loud one.
 */
function MissingAgreement({
  deal,
  coverage,
}: {
  deal: DealListItem;
  coverage: SplitCoverage | null;
}): ReactNode {
  if (coverage === null) return null;

  if (coverage === "needs_agreement") {
    return (
      <Notice tone="warn" title="No agreement resolves this deal's split.">
        {`It pins none of its own, and neither ${deal.partner_name} nor any partner above it has a default agreement. Every clause that states no split of its own fails with split_unresolvable until one of those is fixed.`}
      </Notice>
    );
  }

  return (
    <div className={styles.hint}>
      {coverage === "self_stated"
        ? "No partner agreement on file; every clause states its own split."
        : "No partner agreement on file; this deal carries no clause yet."}
    </div>
  );
}

/**
 * What the deal is expected to be worth before it has terms — for the
 * projection report only. Its own card under the deal's fields, and every value
 * badged, because an estimate set beside the contract's own figures must never
 * read as one of them.
 */
function EstimatesCard({
  deal,
  onEdit,
}: {
  deal: DealListItem;
  onEdit: (() => void) | null;
}): ReactNode {
  const money = (minor: number) => formatMoney(minor, deal.currency);
  // Our share leads; the gross somebody typed is the detail under it.
  const shareWord = (pct: number | null): string => {
    if (pct === null) return "";
    const source =
      deal.estimated_share_source === "assumed"
        ? "assumed — no agreement on file"
        : deal.estimated_share_source === "deal"
          ? "this deal's share"
          : "the agreement's share";
    return `${formatPercent(pct)}, ${source}`;
  };
  const ours = (gross: number | null, pct: number | null): string =>
    gross === null ? "Not estimated" : pct === null ? money(gross) : money(splitMinor(gross, pct));
  const detail = (gross: number | null, pct: number | null, what: string): string =>
    gross === null ? what : `Our share at ${shareWord(pct)} of ${money(gross)} ${what}`;
  const rows: ReadonlyArray<{ label: string; value: string; editLabel: string; hint: string }> = [
    {
      label: "First year, our share",
      value:
        deal.estimated_first_year_our_share_minor === null
          ? "Not estimated"
          : money(deal.estimated_first_year_our_share_minor),
      editLabel: "Edit the estimates",
      hint: "Implementation plus twelve months of recurring, after the partner's share.",
    },
    {
      label: "Implementation (one-time)",
      value: ours(deal.estimated_implementation_minor, deal.estimated_implementation_share_pct),
      editLabel: "Edit the estimated implementation fee",
      hint: detail(
        deal.estimated_implementation_minor,
        deal.estimated_implementation_share_pct,
        "one-time, entered before any partner share.",
      ),
    },
    {
      label: "Monthly recurring",
      value: ours(deal.estimated_recurring_monthly_minor, deal.estimated_recurring_share_pct),
      editLabel: "Edit the estimated monthly recurring",
      hint: detail(
        deal.estimated_recurring_monthly_minor,
        deal.estimated_recurring_share_pct,
        "a month once live, entered before any partner share.",
      ),
    },
    {
      label: "Go-live",
      value:
        deal.estimated_go_live === null
          ? "Scheduled by capacity"
          : formatCalendarDate(deal.estimated_go_live),
      editLabel: "Edit the estimated go-live",
      hint: "Blank lets the projection schedule it by go-live capacity.",
    },
  ];
  return (
    <Card>
      <CardBody>
        <div className={styles.fields} role="group" aria-label="Estimates">
          <div className={styles.estimatesHeading}>
            Estimates
            <span className={styles.estimatesNote}>
              Our share, after the partner's. Used only for projections until the deal has contract
              terms.
            </span>
          </div>
          {rows.map((row) => (
            <div key={row.label} className={styles.field}>
              <div className={`${styles.label} ${styles.inlineGroup}`}>
                {row.label}
                <Badge shape="tag" tone="warn">
                  Estimate
                </Badge>
              </div>
              <EditableValue value={row.value} editLabel={row.editLabel} onEdit={onEdit} />
              <div className={styles.hint} title={row.hint}>
                {row.hint}
              </div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Where the end customer is — and which of the THREE states this deal is in,
 * because they are three different pieces of work and only one of them is
 * "done".
 *
 *   * No country recorded. The deal sits in the revenue-by-region report's
 *     unlocated bucket, which is the reason that bucket exists.
 *   * A country (and maybe a region and a city) but no coordinates. The report
 *     groups it correctly; a map has nothing to draw. This is the ordinary
 *     state of a deal someone filled in properly but not precisely, and it is
 *     the one an empty-vs-filled rendering would hide.
 *   * Both. Placed and plottable.
 *
 * `dealHasLocation` and `dealHasCoordinates` answer those two questions and are
 * deliberately not the same predicate — which is exactly why the middle state
 * gets a marker of its own rather than looking like either neighbour.
 */
function CustomerLocation({
  deal,
  onEdit,
}: {
  deal: DealListItem;
  onEdit: (() => void) | null;
}): ReactNode {
  const located = dealHasLocation(deal);

  return (
    <div className={`${styles.field} ${styles.locationField}`}>
      <div className={styles.label}>Customer location</div>
      <div className={`${styles.value} ${styles.inlineGroup}`}>
        {located ? (
          <EditableValue
            value={locationText(deal)}
            editLabel="Edit the customer's location"
            onEdit={onEdit}
          />
        ) : (
          <Badge shape="chip" tone="neutral">
            No location recorded
          </Badge>
        )}

        {dealHasCoordinates(deal) ? (
          <span className={`${styles.coordinates} mono`}>
            {`${deal.customer_lat}, ${deal.customer_lng}`}
          </span>
        ) : located ? (
          <Badge shape="tag" tone="warn">
            No coordinates
          </Badge>
        ) : null}

        {!located && onEdit !== null && (
          <button type="button" className={styles.addLocation} onClick={onEdit}>
            Add a location
          </button>
        )}
      </div>
      <div className={styles.hint}>{locationHint(deal)}</div>
    </div>
  );
}

function locationText(deal: DealListItem): string {
  const parts = [deal.customer_city, deal.customer_region].filter(
    (part): part is string => part !== null && part !== "",
  );
  // The country CODE stays on screen beside its name: the code is what is
  // stored and what the region report groups on, so a reader comparing this
  // deal against a report row is comparing the same string.
  if (deal.customer_country !== null) parts.push(countryLabel(deal.customer_country));
  return parts.join(", ");
}

function locationHint(deal: DealListItem): string {
  if (!dealHasLocation(deal)) {
    return "Nobody has said where this customer is, so this deal's revenue lands in the unlocated bucket of the revenue-by-region report rather than in a place.";
  }
  return dealHasCoordinates(deal)
    ? "Placed and plottable. The coordinates were entered by hand — nothing here geocodes."
    : "The region report can group this deal; a map cannot draw it. Coordinates are optional and entered by hand.";
}

interface FieldProps {
  label: string;
  children: ReactNode;
  /** What the value MEANS, when the value alone doesn't say it. */
  hint?: string;
}

function Field({ label, children, hint }: FieldProps): ReactNode {
  return (
    <div className={styles.field}>
      <div className={styles.label}>{label}</div>
      {children}
      {/* Clamped to two lines by `.hint`; the title carries the rest. */}
      {hint !== undefined && (
        <div className={styles.hint} title={hint}>
          {hint}
        </div>
      )}
    </div>
  );
}

interface EditableValueProps {
  value: string;
  /** Says what the click DOES — "Edit the renewal date", not "Sep 1, 2028". */
  editLabel: string;
  /** Null when this reader may not edit, which renders it as plain text. */
  onEdit: (() => void) | null;
  className?: string;
}

function EditableValue({ value, editLabel, onEdit, className }: EditableValueProps): ReactNode {
  if (onEdit === null) {
    return <div className={[styles.value, className].filter(Boolean).join(" ")}>{value}</div>;
  }

  return (
    <button
      type="button"
      aria-label={editLabel}
      className={[styles.editable, className].filter(Boolean).join(" ")}
      onClick={onEdit}
    >
      {value}
    </button>
  );
}

/**
 * The fact a statement line is short of.
 *
 * A line that counts something wants a QUANTITY and prices it from its own
 * rule; a line whose unit is money, or which counts the period itself, wants
 * the amount outright. Both are one `deal_facts` row and one form — the kind
 * only decides which field the form opens on.
 */
function factFromStatementLine(
  deal: DealListItem,
  line: DealStatementLine,
  period: string,
  pools: readonly PrepaidEntitlement[],
): FactPrefill {
  const wantsAmount =
    line.unit === "currency_minor" || line.unit === "period" || line.source === "deal";
  return {
    dealId: deal.id,
    partnerId: deal.partner_id,
    termLineId: line.term_line_id,
    period,
    kind: wantsAmount ? "amount" : "quantity",
    pools,
    unit: line.unit,
    currency: deal.currency,
    label: line.label,
  };
}

/** "Record a fact" with nothing but the deal and the month behind it. */
function blankFact(
  deal: DealListItem,
  period: string,
  pools: readonly PrepaidEntitlement[],
): FactPrefill {
  return {
    dealId: deal.id,
    partnerId: deal.partner_id,
    termLineId: null,
    period,
    kind: "amount",
    pools,
    unit: null,
    currency: deal.currency,
    label: null,
  };
}
